import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Paper,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Save, 
  Download, 
  FileText,
  RefreshCw,
  PlusCircle,
  MinusCircle,
  Highlighter,
  Type,
  Code,
  FileCog,
  Edit,
  X
} from 'lucide-react';
import agtEduService from '../services/agtEduService';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';

// Componente para visualizar cambios de documento
const DocumentChangesVisualizer = ({ changes, colors }) => {
  if (!changes || !changes.changes || !Array.isArray(changes.changes)) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No hay cambios específicos registrados todavía.
      </Alert>
    );
  }

  // Agrupar cambios por tipo
  const changesByType = changes.changes.reduce((acc, change) => {
    if (!acc[change.type]) {
      acc[change.type] = [];
    }
    acc[change.type].push(change);
    return acc;
  }, {});

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'replace':
        return <RefreshCw size={18} />;
      case 'insert':
        return <PlusCircle size={18} />;
      case 'delete':
        return <MinusCircle size={18} />;
      case 'style':
        return <Type size={18} />;
      case 'formula':
        return <Code size={18} />;
      case 'format':
        return <FileCog size={18} />;
      case 'annotation':
        return <Highlighter size={18} />;
      case 'highlight':
        return <Highlighter size={18} />;
      default:
        return <Edit size={18} />;
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        Se han detectado {changes.changes.length} cambios para aplicar al documento {changes.documentType.toUpperCase()}.
      </Alert>
      
      {Object.entries(changesByType).map(([type, typeChanges]) => (
        <Paper
          key={type}
          elevation={1}
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: colors.backgroundSecondary
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {getChangeTypeIcon(type)}
            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 500 }}>
              {type.charAt(0).toUpperCase() + type.slice(1)} ({typeChanges.length})
            </Typography>
          </Box>
          
          <List dense sx={{ bgcolor: 'transparent' }}>
            {typeChanges.map((change, index) => {
              // Determinar la ubicación de manera legible
              let location = '';
              if (change.location) {
                if (change.location.paragraph) {
                  location = `Párrafo ${change.location.paragraph}`;
                } else if (change.location.cell) {
                  location = `Celda ${change.location.cell}${change.location.sheet ? ` (Hoja: ${change.location.sheet})` : ''}`;
                } else if (change.location.slide) {
                  location = `Diapositiva ${change.location.slide}`;
                } else if (change.location.page) {
                  location = `Página ${change.location.page}`;
                } else if (change.location.line) {
                  location = `Línea ${change.location.line}`;
                }
              }
              
              return (
                <ListItem
                  key={index}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    backgroundColor: `${colors.cardBackground}80`,
                    flexDirection: location ? 'column' : 'row',
                    alignItems: location ? 'flex-start' : 'center'
                  }}
                >
                  {location && (
                    <Box sx={{ width: '100%', display: 'flex', mb: 1 }}>
                      <Chip
                        size="small"
                        label={location}
                        sx={{
                          backgroundColor: `${colors.accent}20`,
                          color: colors.accent
                        }}
                      />
                    </Box>
                  )}
                  
                  <ListItemText
                    primary={
                      <Box>
                        {change.type === 'replace' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Typography
                              component="div"
                              sx={{
                                textDecoration: 'line-through',
                                color: colors.error,
                                mb: 1,
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: `${colors.error}15`,
                                width: '100%'
                              }}
                            >
                              {change.oldContent || ''}
                            </Typography>
                            <Typography
                              component="div"
                              sx={{
                                color: colors.success,
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: `${colors.success}15`,
                                width: '100%'
                              }}
                            >
                              {change.content || change.value || ''}
                            </Typography>
                          </Box>
                        ) : change.type === 'insert' ? (
                          <Typography
                            component="div"
                            sx={{
                              color: colors.success,
                              p: 1,
                              borderRadius: 1,
                              backgroundColor: `${colors.success}15`
                            }}
                          >
                            {change.content || change.value || ''}
                          </Typography>
                        ) : change.type === 'delete' ? (
                          <Typography
                            component="div"
                            sx={{
                              textDecoration: 'line-through',
                              color: colors.error,
                              p: 1,
                              borderRadius: 1,
                              backgroundColor: `${colors.error}15`
                            }}
                          >
                            {change.content || change.value || ''}
                          </Typography>
                        ) : change.type === 'style' ? (
                          <Box>
                            <Typography component="span" sx={{ color: colors.textSecondary, mr: 1 }}>
                              Estilo:
                            </Typography>
                            {typeof change.content === 'object' ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {Object.entries(change.content).map(([prop, value]) => (
                                  <Chip 
                                    key={prop}
                                    label={`${prop}: ${value}`}
                                    size="small"
                                    sx={{ 
                                      backgroundColor: `${colors.accent}20`, 
                                      color: colors.textDefault 
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography>
                                {change.content || change.value || ''}
                              </Typography>
                            )}
                          </Box>
                        ) : change.type === 'formula' ? (
                          <Box sx={{ 
                            backgroundColor: `${colors.accent}15`,
                            p: 1,
                            borderRadius: 1,
                            fontFamily: 'monospace'
                          }}>
                            <Typography sx={{ color: colors.accent }}>
                              {change.content || change.value || ''}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography>
                            {change.content || change.value || 'Cambio sin contenido específico'}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Paper>
      ))}
    </Box>
  );
};

const DocumentViewer = ({ 
  file, 
  changes,
  colors,
  onApplyChanges
}) => {
  const [documentContent, setDocumentContent] = useState(null);
  const [activeView, setActiveView] = useState('preview');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState('unknown');
  const [htmlContent, setHtmlContent] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [changesApplied, setChangesApplied] = useState(false);
  const [selectionActive, setSelectionActive] = useState(false);
  
  const documentContentRef = useRef(null);

  // Efecto para depurar el estado actual
  useEffect(() => {
    console.log("Estado actual:", {
      fileType,
      htmlContent: htmlContent ? "Definido" : "No definido",
      previewUrl: previewUrl ? "Definido" : "No definido",
      changes: changes ? "Definido" : "No definido"
    });
  }, [fileType, htmlContent, previewUrl, changes]);

  const determineFileType = useCallback((file) => {
    if (!file || !file.name) {
      setFileType('unknown');
      return;
    }
  
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
      setFileType('word');
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || fileName.endsWith('.csv')) {
      setFileType('excel');
    } else if (fileName.endsWith('.pdf')) {
      setFileType('pdf');
    } else if (fileName.endsWith('.pptx') || fileName.endsWith('.ppt')) {
      setFileType('powerpoint');
    } else if (fileName.endsWith('.txt') || fileName.endsWith('.md')) {
      setFileType('text');
    } else {
      setFileType('unknown');
    }
  }, [setFileType]);

  // Wrap all document processing functions in useCallback
  const loadWordDocument = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      
      // Usar mammoth para convertir DOCX a HTML
      const result = await mammoth.convertToHtml({ arrayBuffer });
      console.log("Word HTML generado:", result.value.substring(0, 100) + "..."); // Depuración
      
      // Mejorar el HTML para edición
      let enhancedHtml = result.value;
      
      // Asegurarse de que todos los párrafos tengan un ID para facilitar la selección
      if (enhancedHtml.indexOf('<p id="') === -1) {
        let paragraphCount = 0;
        enhancedHtml = enhancedHtml.replace(/<p>/g, () => {
          paragraphCount++;
          return `<p id="para-${paragraphCount}" data-paragraph="${paragraphCount}">`;
        });
      }
      
      setHtmlContent(enhancedHtml);
    } catch (error) {
      console.error('Error al procesar documento Word:', error);
      setError(`Error al procesar documento Word: ${error.message}`);
    }
  }, [setHtmlContent, setError]);

  const loadExcelDocument = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      
      // Usar SheetJS para procesar el archivo Excel
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Convertir la primera hoja a HTML
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      let html = XLSX.utils.sheet_to_html(firstSheet);
      
      // Mejorar el HTML para edición: Añadir atributos data-cell-id a cada celda
      html = html.replace(/<td([^>]*)>(.+?)<\/td>/g, (match, attrs, content) => {
        // Extraer el id de celda (si existe) o usar un valor predeterminado
        const cellMatch = attrs.match(/id="([^"]+)"/);
        const cellId = cellMatch ? cellMatch[1].replace('_', '') : 'A1';
        
        return `<td${attrs} data-cell-id="${cellId}">${content}</td>`;
      });
      
      console.log("Excel HTML generado:", html.substring(0, 100) + "..."); // Depuración
      setHtmlContent(html);
    } catch (error) {
      console.error('Error al procesar documento Excel:', error);
      setError(`Error al procesar documento Excel: ${error.message}`);
    }
  }, [setHtmlContent, setError]);

  const loadPowerPointDocument = useCallback((url) => {
    // Para PowerPoint, podríamos usar un servicio de terceros como Microsoft Office Online
    // Por ahora, simplemente configuramos una URL para el iframe que apunte a un visor
    const officeOnlineViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    setHtmlContent(`<iframe src="${officeOnlineViewerUrl}" width="100%" height="100%" frameborder="0"></iframe>`);
  }, [setHtmlContent]);

  const loadTextDocument = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      const text = await response.text();
      
      // Convertir el texto a HTML con líneas numeradas para facilitar la edición
      const lines = text.split('\n');
      let htmlLines = '';
      
      lines.forEach((line, index) => {
        htmlLines += `<div class="text-line" data-line="${index + 1}">${line || ' '}</div>`;
      });
      
      const enhancedHtml = `
        <div class="text-document">
          <style>
            .text-document {
              font-family: monospace;
              white-space: pre-wrap;
              line-height: 1.5;
            }
            .text-line {
              padding: 2px 0;
              position: relative;
            }
            .text-line:hover {
              background-color: #f5f5f5;
            }
            .text-line::before {
              content: attr(data-line);
              position: absolute;
              left: -40px;
              width: 30px;
              text-align: right;
              color: #888;
              font-size: 0.8em;
            }
          </style>
          ${htmlLines}
        </div>
      `;
      
      setHtmlContent(enhancedHtml);
    } catch (error) {
      console.error('Error al cargar documento de texto:', error);
      setError(`Error al cargar documento de texto: ${error.message}`);
    }
  }, [setHtmlContent, setError]);

  // Aplicar cambios a documentos Word
  const applyWordDocumentChanges = useCallback((changes) => {
    // En una implementación real, aplicaríamos los cambios específicos al HTML
    // Aquí implementamos una versión simplificada para visualización
    
    // Añadir un estilo CSS para los cambios
    const cssStyles = `
      <style>
        .agt-inserted {
          background-color: rgba(0, 255, 0, 0.2);
          text-decoration: none;
        }
        .agt-deleted {
          background-color: rgba(255, 0, 0, 0.2);
          text-decoration: line-through;
        }
        .agt-replaced {
          background-color: rgba(255, 255, 0, 0.2);
        }
        .agt-styled {
          border: 1px dashed blue;
        }
        .agt-changes-summary {
          background-color: #f0f8ff;
          border-left: 3px solid #007bff;
          padding: 10px;
          margin: 15px 0;
          font-family: Arial, sans-serif;
        }
        .agt-changes-summary h3 {
          margin-top: 0;
          color: #007bff;
        }
        .agt-changes-summary ul {
          margin-bottom: 0;
        }
      </style>
    `;
    
    // Crear un resumen de cambios
    const changesSummary = `
      <div class="agt-changes-summary">
        <h3>Cambios aplicados</h3>
        <ul>
          ${changes.changes.map(change => {
            let changeDesc = '';
            let location = '';
            
            if (change.location) {
              if (change.location.paragraph) {
                location = `párrafo ${change.location.paragraph}`;
              } else if (change.location.page) {
                location = `página ${change.location.page}`;
              }
            }
            
            if (change.type === 'replace') {
              changeDesc = `Reemplazado "${change.oldContent || '...'}" por "${change.content || '...'}"`;
            } else if (change.type === 'insert') {
              changeDesc = `Insertado "${change.content || '...'}"`;
            } else if (change.type === 'delete') {
              changeDesc = `Eliminado "${change.content || '...'}"`;
            } else if (change.type === 'style') {
              changeDesc = `Estilo modificado: ${change.style || '...'} → ${change.content || '...'}`;
            } else {
              changeDesc = `${change.type}: ${change.content || '...'}`;
            }
            
            return `<li>${changeDesc}${location ? ` (en ${location})` : ''}</li>`;
          }).join('')}
        </ul>
      </div>
    `;
    
    // Añadir los estilos si no están presentes
    let newHtmlContent = htmlContent;
    if (!newHtmlContent.includes('<style>')) {
      newHtmlContent = cssStyles + newHtmlContent;
    }
    
    // Añadir el resumen al final del documento
    newHtmlContent = newHtmlContent + changesSummary;
    
    // Actualizar el contenido HTML
    setHtmlContent(newHtmlContent);
  }, [htmlContent, setHtmlContent]);
  
  // Aplicar cambios a hojas de Excel
  const applyExcelDocumentChanges = useCallback((changes) => {
    // Implementación simplificada con visualización de cambios
    const changesTable = `
      <div style="background-color: #e2f7df; padding: 10px; margin: 10px 0; border-left: 3px solid green;">
        <p style="font-weight: bold; margin: 0 0 8px 0;">Cambios aplicados en Excel:</p>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Celda</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Tipo</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Valor</th>
            </tr>
          </thead>
          <tbody>
            ${changes.changes.map(change => {
              const cell = change.location?.cell || 'N/A';
              const sheet = change.location?.sheet || 'Hoja1';
              const type = change.type || 'N/A';
              const value = (change.value !== undefined) ? change.value : 
                          (change.content !== undefined) ? change.content : 'N/A';
              
              return `
                <tr>
                  <td style="border: 1px solid #ddd; padding: 8px;">${sheet}!${cell}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${type}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${value}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
    
    // Añadir al HTML existente
    setHtmlContent(prevHtml => prevHtml + changesTable);
  }, [setHtmlContent]);
  
  // Aplicar cambios a PowerPoint
  const applyPowerPointChanges = useCallback((changes) => {
    // En implementación real, podríamos mostrar una visualización de las diapositivas
    // con los cambios aplicados. Por ahora, solo actualizamos el estado.
    console.log('Cambios en PowerPoint aplicados:', changes);
    
    // Agregar mensaje al HTML
    const changesSummary = `
      <div style="background-color: #f0f8ff; border-left: 3px solid #007bff; padding: 10px; margin: 15px 0; font-family: Arial, sans-serif;">
        <h3 style="margin-top: 0; color: #007bff;">Cambios aplicados en PowerPoint</h3>
        <p>Se han aplicado ${changes.changes.length} cambios a la presentación.</p>
        <ul>
          ${changes.changes.map(change => {
            let location = change.location?.slide ? `Diapositiva ${change.location.slide}` : '';
            let element = change.location?.element ? `, ${change.location.element}` : '';
            let action = '';
            
            switch (change.type) {
              case 'replace':
                action = 'Reemplazado texto';
                break;
              case 'insert':
                action = 'Insertado contenido';
                break;
              case 'delete':
                action = 'Eliminado contenido';
                break;
              case 'style':
                action = 'Aplicado estilo';
                break;
              default:
                action = change.type;
                break;
            }
            
            return `<li>${action} en ${location}${element}</li>`;
          }).join('')}
        </ul>
      </div>
    `;
    
    // Actualizar el HTML si existe, o crearlo
    if (htmlContent) {
      setHtmlContent(prevHtml => prevHtml + changesSummary);
    } else {
      setHtmlContent(changesSummary);
    }
  }, [htmlContent, setHtmlContent]);
  
  // Aplicar cambios a PDF
  const applyPdfChanges = useCallback((changes) => {
    // En implementación real, podríamos mostrar anotaciones visuales en el PDF
    // Por ahora, solo actualizamos el estado
    console.log('Cambios en PDF aplicados:', changes);
    
    // Crear un mensaje que indique qué cambios se hicieron
    const message = `
      <div style="position: fixed; top: 0; left: 0; right: 0; z-index: 1000; background-color: #e2f7df; padding: 10px; border-bottom: 1px solid green; text-align: center;">
        <strong>Anotaciones aplicadas:</strong> ${changes.changes.length} cambios en el documento PDF.
        <p style="margin: 5px 0 0 0; font-size: 0.8em;">Nota: Las anotaciones se muestran en el panel "Cambios" para revisión detallada.</p>
      </div>
    `;
    
    // Añadir este mensaje al comienzo del contenido HTML
    if (htmlContent) {
      setHtmlContent(message + htmlContent);
    } else {
      setHtmlContent(message);
    }
  }, [htmlContent, setHtmlContent]);
  
  // Aplicar cambios a documentos de texto
  const applyTextDocumentChanges = useCallback((changes) => {
    try {
      // Si no hay contenido HTML, no podemos aplicar cambios
      if (!htmlContent) return;
      
      // Extraer el texto del HTML (asumiendo que está en un elemento <pre>)
      let textContent = htmlContent;
      
      // Si el HTML contiene una etiqueta <pre>, extraer solo el contenido
      if (textContent.includes('<pre')) {
        const preMatch = textContent.match(/<pre[^>]*>([\s\S]*?)<\/pre>/);
        if (preMatch && preMatch[1]) {
          textContent = preMatch[1];
        }
      }
      
      // Eliminar cualquier HTML que pueda quedar
      textContent = textContent.replace(/<[^>]*>/g, '');
      
      // Convertir el texto a un array de líneas
      const lines = textContent.split('\n');
      
      // Crear una copia del array para modificar
      const modifiedLines = [...lines];
      
      // Procesar cada cambio
      changes.changes.forEach(change => {
        // Obtener el número de línea (ajustar índice a base 0)
        const lineIndex = change.location && change.location.line 
          ? change.location.line - 1 
          : -1;
        
        // Si no hay un índice de línea válido, omitir este cambio
        if (lineIndex < 0 || lineIndex >= modifiedLines.length) return;
        
        // Aplicar el cambio según su tipo
        switch (change.type) {
          case 'replace':
            if (change.oldContent && change.content) {
              modifiedLines[lineIndex] = modifiedLines[lineIndex].replace(
                change.oldContent,
                change.content
              );
            }
            break;
          case 'insert':
            if (change.content) {
              modifiedLines[lineIndex] += change.content;
            }
            break;
          case 'delete':
            if (change.content) {
              modifiedLines[lineIndex] = modifiedLines[lineIndex].replace(
                change.content,
                ''
              );
            }
            break;
          default:
            // Caso por defecto agregado
            console.log(`Tipo de cambio no manejado: ${change.type}`);
            break;
        }
      });
      
      // Crear nuevo HTML con los cambios aplicados
      const modifiedText = modifiedLines.join('\n');
      
      // Estilo para mostrar las diferencias
      const diffStyles = `
        <style>
          .diff-container {
            font-family: monospace;
            white-space: pre-wrap;
            padding: 10px;
            background-color: #f5f5f5;
            border-radius: 4px;
          }
          .diff-added {
            background-color: #e6ffed;
            border-left: 2px solid #34d058;
            padding-left: 5px;
          }
          .diff-removed {
            background-color: #ffeef0;
            border-left: 2px solid #d73a49;
            padding-left: 5px;
            text-decoration: line-through;
          }
          .diff-unchanged {
            padding-left: 7px;
          }
          .diff-info {
            background-color: #f0f8ff;
            border-left: 3px solid #007bff;
            padding: 10px;
            margin: 15px 0;
            font-family: Arial, sans-serif;
          }
        </style>
      `;
      
      // Crear un resumen visual de los cambios
      const changesSummary = `
        <div class="diff-info">
          <h3 style="margin-top: 0; color: #007bff;">Cambios aplicados</h3>
          <ul>
            ${changes.changes.map(change => {
              const lineNum = change.location?.line || 'Desconocida';
              let changeDesc = '';
              
              if (change.type === 'replace') {
                changeDesc = `Reemplazado "${change.oldContent || '...'}" por "${change.content || '...'}"`;
              } else if (change.type === 'insert') {
                changeDesc = `Insertado "${change.content || '...'}"`;
              } else if (change.type === 'delete') {
                changeDesc = `Eliminado "${change.content || '...'}"`;
              } else {
                changeDesc = `${change.type}: ${change.content || '...'}`;
              }
              
              return `<li>${changeDesc} (línea ${lineNum})</li>`;
            }).join('')}
          </ul>
        </div>
      `;
      
      // Crear HTML con el texto modificado y resumen de cambios
      const newHtmlContent = `
        ${diffStyles}
        ${changesSummary}
        <div class="diff-container">
          <pre>${modifiedText}</pre>
        </div>
      `;
      
      // Actualizar el contenido HTML
      setHtmlContent(newHtmlContent);
      
    } catch (error) {
      console.error('Error al aplicar cambios a documento de texto:', error);
      setError(`Error al aplicar cambios: ${error.message}`);
    }
  }, [htmlContent, setHtmlContent, setError]);

  // Wrap loadDocument in useCallback with all required dependencies
  const loadDocument = useCallback(async (file) => {
    try {
      setLoading(true);
      setError(null);
      let url;
      
      if (file.url) {
        url = file.url;
      } else if (file.s3Key) {
        // Usar el método refreshFileUrl de agtEduService
        const response = await agtEduService.refreshFileUrl(file.id, 3600);
        url = response.data?.url || null;
      }
      
      setPreviewUrl(url);
      
      // Procesar el archivo según su tipo
      if (url) {
        if (fileType === 'word') {
          await loadWordDocument(url);
        } else if (fileType === 'excel') {
          await loadExcelDocument(url);
        } else if (fileType === 'powerpoint') {
          loadPowerPointDocument(url);
        } else if (fileType === 'text') {
          await loadTextDocument(url);
        }
      }
      
      setDocumentContent({
        content: "Documento cargado con éxito",
        type: fileType,
        sections: []
      });
    } catch (error) {
      console.error('Error al cargar el documento:', error);
      setError(`Error al cargar el documento: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [
    fileType, 
    setLoading, 
    setError, 
    setPreviewUrl, 
    setDocumentContent, 
    loadWordDocument, 
    loadExcelDocument, 
    loadPowerPointDocument, 
    loadTextDocument
  ]);

  useEffect(() => {
    if (file) {
      // Determinar el tipo de archivo
      determineFileType(file);
      
      // Cargar el contenido del documento
      loadDocument(file);
      
      // Resetear el estado de cambios aplicados
      setChangesApplied(false);
      
      // Asegurarse de que la vista activa sea preview al abrir
      setActiveView('preview');
      
      // Resetear estados de edición
      setSelectionActive(false);
    }
  }, [file, loadDocument, determineFileType, setChangesApplied, setActiveView, setSelectionActive]);

  // Wrap applyChangesToDocument in useCallback with all required dependencies
  const applyChangesToDocument = useCallback((changes) => {
    // Esta función aplicaría los cambios recibidos del asistente al documento
    console.log('Aplicando cambios al documento:', changes);
    
    // Verificar que los cambios tienen la estructura correcta
    if (!changes || !changes.documentType || !changes.changes || !Array.isArray(changes.changes)) {
      console.warn('Formato de cambios inválido:', changes);
      return;
    }
    
    // Verificar que el tipo de documento coincide con el archivo actual
    if (fileType !== changes.documentType && 
        !(fileType === 'text' && changes.documentType === 'word')) {
      console.warn(`Tipo de documento no coincide: Archivo=${fileType}, Cambios=${changes.documentType}`);
      return;
    }
    
    // Marcar los cambios como aplicados para evitar aplicarlos múltiples veces
    setChangesApplied(true);
    
    // Actualizar estado con los cambios estructurados correctamente
    setDocumentContent(prevContent => ({
      ...prevContent,
      content: "Documento actualizado con los cambios detectados",
      type: changes.documentType,
      changes: changes.changes,
      updatedAt: new Date().toISOString(),
      updatedContent: changes
    }));
    
    // Aplicar cambios específicos según el tipo de documento
    if (fileType === 'word' && htmlContent) {
      applyWordDocumentChanges(changes);
    } else if (fileType === 'excel' && htmlContent) {
      applyExcelDocumentChanges(changes);
    } else if (fileType === 'powerpoint') {
      applyPowerPointChanges(changes);
    } else if (fileType === 'pdf') {
      applyPdfChanges(changes);
    } else if (fileType === 'text') {
      applyTextDocumentChanges(changes);
    }
    
    console.log('Cambios aplicados correctamente al documento');
    
    // Cambiar automáticamente a la vista de previsualización para mostrar los cambios
    setActiveView('preview');
  }, [
    fileType, 
    htmlContent, 
    setChangesApplied, 
    setDocumentContent, 
    setActiveView,
    applyWordDocumentChanges,
    applyExcelDocumentChanges,
    applyPowerPointChanges,
    applyPdfChanges,
    applyTextDocumentChanges
  ]);

  useEffect(() => {
    // Aplicar cambios al documento si se reciben
    if (changes && documentContent && !changesApplied) {
      applyChangesToDocument(changes);
    }
  }, [changes, documentContent, changesApplied, applyChangesToDocument]);

  const handleApplyChanges = useCallback(() => {
    if (onApplyChanges) {
      onApplyChanges(documentContent);
    }
  }, [onApplyChanges, documentContent]);

  const handleDownloadWithChanges = useCallback(() => {
    // Ejemplo básico: generar un HTML con los cambios y descargarlo
    if (!htmlContent || !file) return;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file.name}_con_cambios.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [htmlContent, file]);

  // Función para habilitar/deshabilitar la selección de texto
  const toggleSelectionMode = useCallback(() => {
    setSelectionActive(prevState => !prevState);
  }, [setSelectionActive]);

  const renderPreview = useCallback(() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <LinearProgress sx={{ width: '50%' }} />
        </Box>
      );
    }
    
    if (!previewUrl) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography sx={{ color: colors.textSecondary }}>
            No se pudo cargar la vista previa
          </Typography>
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
          <Button 
            variant="outlined" 
            size="small" 
            startIcon={<Download size={16} />}
            href={previewUrl}
            target="_blank"
            sx={{ mt: 1 }}
          >
            Descargar archivo original
          </Button>
        </Alert>
      );
    }
    
    // Si tenemos HTML generado localmente y es un documento editable
    if (htmlContent && (fileType === 'word' || fileType === 'text' || fileType === 'excel')) {
      // Aplicar estilos específicos cuando el modo de selección está activo
      const selectionModeStyles = selectionActive ? {
        cursor: 'text',
        userSelect: 'text',
        border: `2px dashed ${colors.accent}`,
        position: 'relative'
      } : {};
      
      return (
        <Paper 
          elevation={2} 
          sx={{ 
            width: '100%', 
            height: '100%', 
            overflow: 'auto', 
            p: 4,
            backgroundColor: '#ffffff',
            ...selectionModeStyles
          }}
        >
          {selectionActive && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                padding: 1,
                backgroundColor: `${colors.accent}33`,
                zIndex: 10,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Typography variant="body2" sx={{ color: colors.accent, fontWeight: 'bold' }}>
                Modo selección: Selecciona el texto que deseas editar
              </Typography>
              
              <IconButton
                size="small"
                onClick={toggleSelectionMode}
                sx={{ ml: 2, color: colors.accent }}
              >
                <X size={16} />
              </IconButton>
            </Box>
          )}
          
          <div 
            ref={documentContentRef}
            dangerouslySetInnerHTML={{ __html: htmlContent }} 
          />
        </Paper>
      );
    } else if (fileType === 'word' || fileType === 'excel' || fileType === 'powerpoint') {
      // Si no tenemos HTML generado, usar Office Online Viewer
      const officeOnlineViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`;
      return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <iframe 
            src={officeOnlineViewerUrl} 
            width="100%" 
            height="100%" 
            style={{ border: 'none', background: '#fff' }}
            title="Office Document Viewer"
          />
        </Box>
      );
    } else if (fileType === 'pdf') {
      return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <iframe 
            src={`${previewUrl}#toolbar=0`} 
            width="100%" 
            height="100%" 
            style={{ border: 'none', background: '#fff' }}
            title="PDF Viewer"
          />
        </Box>
      );
    } else if (fileType === 'text' && htmlContent) {
      return (
        <Paper elevation={2} sx={{ width: '100%', height: '100%', overflow: 'auto', p: 4 }}>
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </Paper>
      );
    } else {
      // Fallback para otros tipos de archivo
      return (
        <Paper 
          elevation={2} 
          sx={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            overflow: 'auto',
            p: 4
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <FileText size={40} color={colors.textSecondary} />
            <Typography sx={{ mt: 2, color: colors.textDefault }}>
              Vista previa no disponible para este tipo de archivo
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<Download size={16} />}
              href={previewUrl}
              target="_blank"
              sx={{ mt: 2 }}
            >
              Descargar archivo
            </Button>
          </Box>
        </Paper>
      );
    }
  }, [
    loading, 
    previewUrl, 
    error, 
    htmlContent, 
    fileType, 
    selectionActive, 
    colors, 
    toggleSelectionMode, 
    documentContentRef
  ]);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      backgroundColor: colors.backgroundTertiary
    }}>
      {/* Header con información del documento */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        backgroundColor: colors.backgroundSecondary,
        borderBottom: `1px solid ${colors.borderColor}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, color: colors.textDefault }}>
            {file?.name || 'Documento sin título'}
          </Typography>
          {changes && (
            <Chip 
              label={`${changes.changes?.length || 0} cambios`} 
              size="small" 
              color="success"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Modo edición">
            <Button
              variant={selectionActive ? "contained" : "outlined"}
              startIcon={<Edit size={16} />}
              onClick={toggleSelectionMode}
              size="small"
              sx={{ 
                backgroundColor: selectionActive ? colors.accent : 'transparent',
                color: selectionActive ? '#ffffff' : colors.textDefault
              }}
            >
              Editar
            </Button>
          </Tooltip>
          
          <Tooltip title="Descargar con cambios">
            <Button
              variant="outlined"
              startIcon={<Download size={16} />}
              onClick={handleDownloadWithChanges}
              disabled={!htmlContent}
              size="small"
            >
              Descargar
            </Button>
          </Tooltip>
          
          <Tooltip title="Aplicar cambios al documento">
            <Button
              variant="contained"
              startIcon={<Save size={16} />}
              onClick={handleApplyChanges}
              disabled={!changes || !changes.changes || changes.changes.length === 0}
              size="small"
              color="success"
            >
              Aplicar Cambios
            </Button>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Tabs para cambiar entre vistas */}
      <Tabs
        value={activeView}
        onChange={(e, newValue) => setActiveView(newValue)}
        sx={{ 
          backgroundColor: colors.backgroundSecondary,
          borderBottom: `1px solid ${colors.borderColor}`,
          minHeight: 48
        }}
      >
        <Tab 
          value="preview" 
          label="Vista previa" 
          sx={{ 
            minHeight: 48,
            textTransform: 'none',
            color: activeView === 'preview' ? colors.accent : colors.textSecondary
          }}
        />
        <Tab 
          value="changes" 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Cambios
              {changes && changes.changes && (
                <Chip 
                  label={changes.changes.length} 
                  size="small" 
                  sx={{ ml: 1, height: 20, minWidth: 20 }}
                />
              )}
            </Box>
          }
          sx={{ 
            minHeight: 48,
            textTransform: 'none',
            color: activeView === 'changes' ? colors.accent : colors.textSecondary
          }}
        />
      </Tabs>
      
      {/* Área de contenido principal */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {activeView === 'preview' ? (
          renderPreview()
        ) : (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: '100%', 
                backgroundColor: colors.background,
                borderRadius: 2
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: colors.textDefault }}>
                Cambios detectados
              </Typography>
              
              {changes && changes.changes && changes.changes.length > 0 ? (
                <DocumentChangesVisualizer changes={changes} colors={colors} />
              ) : (
                <Alert severity="info">
                  No se han detectado cambios para este documento. Envía instrucciones en el chat para modificar el documento.
                </Alert>
              )}
              
              {changes && changes.changes && changes.changes.length > 0 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Save size={16} />}
                    onClick={handleApplyChanges}
                  >
                    Aplicar todos los cambios
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DocumentViewer;